// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useState } from 'react';
import {
  ASSUMPTION_FIELDS,
  projectionAssumptionsApi,
  type EffectiveAssumptions,
  type ProjectionAssumptions,
} from '../api/projectionAssumptionsApi';
import { brokerageApi, type Brokerage } from '../api/brokerageApi';

/**
 * Platform-admin editor for the GLOBAL projection-assumption defaults and any
 * brokerage's override (the top two levels of the hierarchy; advisors edit the
 * advisor/relationship levels in the Advisor UI). Sparse, per-key — a blank field
 * means "not set at this level". Edits are audited (PROJECTION_ASSUMPTIONS).
 * See docs/architecture/layered-projection-assumptions.md.
 */
type Target = { kind: 'GLOBAL' } | { kind: 'BROKERAGE'; id: string };

export default function ProjectionAssumptionsConfigPage() {
  const [brokerages, setBrokerages] = useState<Brokerage[]>([]);
  const [target, setTarget] = useState<Target>({ kind: 'GLOBAL' });
  const [form, setForm] = useState<Record<string, string>>({});
  const [inherited, setInherited] = useState<EffectiveAssumptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    brokerageApi.getAll({ size: 200 })
      .then((p) => setBrokerages(p.content))
      .catch(() => { /* picker is optional */ });
  }, []);

  const toForm = (a: ProjectionAssumptions) =>
    setForm(Object.fromEntries(ASSUMPTION_FIELDS.map((f) => {
      const v = a[f.key];
      return [f.key, v == null ? '' : String(v)];
    })));

  const load = useCallback((t: Target) => {
    setLoading(true);
    setError(null);
    setMessage(null);
    const override = t.kind === 'GLOBAL'
      ? projectionAssumptionsApi.getGlobal()
      : projectionAssumptionsApi.getBrokerage(t.id);
    const below = t.kind === 'GLOBAL'
      ? projectionAssumptionsApi.getGlobalInherited()
      : projectionAssumptionsApi.getBrokerageInherited(t.id);
    Promise.all([override, below])
      .then(([o, inh]) => { toForm(o); setInherited(inh); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(target); }, [load, target]);

  const body = (): ProjectionAssumptions => {
    const out: Record<string, number | null> = {};
    for (const f of ASSUMPTION_FIELDS) {
      const raw = form[f.key]?.trim();
      out[f.key] = raw === undefined || raw === '' ? null : Number(raw);
    }
    return out as unknown as ProjectionAssumptions;
  };

  const save = () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    const p = target.kind === 'GLOBAL'
      ? projectionAssumptionsApi.saveGlobal(body())
      : projectionAssumptionsApi.saveBrokerage(target.id, body());
    p.then(() => setMessage(target.kind === 'GLOBAL'
      ? 'Saved. The global default affects every tenant and was audited.'
      : 'Saved. The brokerage override was audited.'))
      .catch((e) => setError(String(e))).finally(() => setSaving(false));
  };

  const clear = () => {
    setSaving(true);
    setError(null);
    const p = target.kind === 'GLOBAL'
      ? projectionAssumptionsApi.clearGlobal()
      : projectionAssumptionsApi.clearBrokerage(target.id);
    p.then(() => { setMessage('Cleared.'); load(target); })
      .catch((e) => setError(String(e))).finally(() => setSaving(false));
  };

  return (
    <div style={{ padding: 24, maxWidth: 880 }} data-testid="projection-assumptions-config-page">
      <h1>Projection Assumptions</h1>
      <p style={{ color: '#666' }}>
        Defaults for cashflow &amp; Future-Values projections. Resolution is per key:
        Relationship → Advisor → Brokerage → Global. Leave a field blank to leave it unset
        at this level.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0' }}>
        <label htmlFor="pa-target">Level</label>
        <select
          id="pa-target"
          data-testid="pa-target-select"
          value={target.kind === 'GLOBAL' ? 'GLOBAL' : target.id}
          onChange={(e) => setTarget(e.target.value === 'GLOBAL'
            ? { kind: 'GLOBAL' } : { kind: 'BROKERAGE', id: e.target.value })}
        >
          <option value="GLOBAL">Global default</option>
          {brokerages.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      {loading && <div data-testid="pa-loading">Loading…</div>}
      {error && <div data-testid="pa-error" style={{ color: '#C0392B' }}>{error}</div>}
      {message && <div data-testid="pa-message" style={{ color: '#1E8E4E' }}>{message}</div>}

      {!loading && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {ASSUMPTION_FIELDS.map((f) => {
              const src = inherited?.sources?.[f.key];
              const inhVal = inherited?.[f.key];
              const showInherited = !form[f.key] && src && src !== 'UNSET';
              return (
                <label key={f.key} style={{ display: 'flex', flexDirection: 'column', fontSize: 13 }}>
                  <span>{f.label} <span style={{ color: '#888' }}>({f.suffix})</span></span>
                  <input
                    type="number"
                    data-testid={`pa-${f.key}`}
                    value={form[f.key] ?? ''}
                    placeholder={showInherited ? `inherited: ${inhVal}` : ''}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  />
                  {showInherited && (
                    <span data-testid={`pa-${f.key}-inherited`} style={{ fontSize: 11, color: '#888' }}>
                      from {src}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button type="button" data-testid="pa-save" onClick={save} disabled={saving}>
              Save{target.kind === 'GLOBAL' ? ' (affects all tenants)' : ''}
            </button>
            <button type="button" data-testid="pa-clear" onClick={clear} disabled={saving}>
              Clear this level
            </button>
          </div>
        </>
      )}
    </div>
  );
}
